import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { createFormatter, type Jurisdiction } from "@/lib/formatters";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown, ChevronRight, FlaskConical, AlertTriangle, TrendingUp, Clock, DollarSign, Lightbulb, Info, Activity } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import {
  type CombinedScenarioInput as CombinedScenarioInputCA,
  type CombinedScenarioResult as CombinedScenarioResultCA,
  type PolicyLoanLedgerRow,
  toISODate,
} from "@/lib/calculationEngine";
import {
  runStressTest as runStressTestCA,
  type StressScenarioInput,
  type StressTestResult,
  type VariableRateStep,
} from "@/lib/stressTestEngine";
import {
  runUSAStressTest,
  type StressScenarioInput as USAStressScenarioInput,
} from "@/lib/usaStressTestEngine";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

// ── Props ──

interface StressTestAnalysisProps {
  scenarioInput: CombinedScenarioInputCA;
  combined: CombinedScenarioResultCA;
  jurisdiction: Jurisdiction;
}

// ── Chart data builder ──

function buildComparisonChartData(
  originalLedger: PolicyLoanLedgerRow[],
  stressedLedger: PolicyLoanLedgerRow[]
) {
  const dataMap = new Map<string, { date: string; original?: number; stressed?: number }>();

  for (const row of originalLedger) {
    const existing = dataMap.get(row.date) || { date: row.date };
    existing.original = row.totalIndebtednessAfterEvent;
    dataMap.set(row.date, existing);
  }

  for (const row of stressedLedger) {
    const existing = dataMap.get(row.date) || { date: row.date };
    existing.stressed = row.totalIndebtednessAfterEvent;
    dataMap.set(row.date, existing);
  }

  return Array.from(dataMap.values()).sort((a, b) => a.date.localeCompare(b.date));
}

// ── Formatters ──

function fmtFull(n: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

// ── Per-scenario chart ──

function ScenarioChart({ result, label, originalLedger }: { result: StressTestResult; label: string; originalLedger: PolicyLoanLedgerRow[] }) {
  const chartData = useMemo(() => buildComparisonChartData(originalLedger, result.stressedPolicyLoan.ledger), [originalLedger, result]);

  return (
    <div className="border border-border rounded-lg p-4 space-y-2 mt-4">
      <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label} — Balance Path Comparison</h4>
      <div className="h-[240px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
            <XAxis dataKey="date" tick={{ fontSize: 9 }} tickFormatter={(d: string) => d.slice(0, 7)} />
            <YAxis tick={{ fontSize: 9 }} tickFormatter={(v: number) => `$${(v / 1000).toFixed(0)}k`} />
            <Tooltip formatter={(v: number) => fmtFull(v)} labelFormatter={(l: string) => `Date: ${l}`} />
            <Legend />
            <Line type="monotone" dataKey="original" stroke="hsl(var(--primary))" name="Original Plan" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="stressed" stroke="hsl(var(--destructive))" name={label} strokeWidth={2} strokeDasharray="6 3" dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ── Scenario Card ──

function ScenarioResultCard({ result, jurisdiction }: { result: StressTestResult; jurisdiction: Jurisdiction }) {
  const { fmt } = createFormatter(jurisdiction);
  const payoffDisplay = result.projectedPayoffDate || "Not paid off within projection";
  const delayDisplay = result.payoffDelayDays > 0
    ? `+${result.payoffDelayDays} days`
    : result.payoffDelayDays === 0 ? "No change" : `${result.payoffDelayDays} days`;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Payoff Date</p>
          <p className="text-sm font-semibold mt-1">{payoffDisplay}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Total Interest</p>
          <p className="text-sm font-semibold mt-1">{fmt(result.totalPolicyLoanInterest)}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">Next Capitalization</p>
          <p className="text-sm font-semibold mt-1">{fmt(result.nextCapitalizationEstimate)}</p>
        </div>
        <div className="bg-muted/50 rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase tracking-wide">End Indebtedness</p>
          <p className="text-sm font-semibold mt-1">{fmt(result.totalIndebtednessAtEnd)}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2 bg-destructive/10 rounded-lg p-3">
          <DollarSign className="h-4 w-4 text-destructive shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Extra Interest</p>
            <p className="text-sm font-semibold text-destructive">
              {result.extraInterest > 0 ? "+" : ""}{fmt(result.extraInterest)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-destructive/10 rounded-lg p-3">
          <Clock className="h-4 w-4 text-destructive shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Payoff Delay</p>
            <p className="text-sm font-semibold text-destructive">{delayDisplay}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 bg-destructive/10 rounded-lg p-3">
          <TrendingUp className="h-4 w-4 text-destructive shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Indebtedness Δ</p>
            <p className="text-sm font-semibold text-destructive">
              {result.indebtednessIncrease > 0 ? "+" : ""}{fmt(result.indebtednessIncrease)}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Corrective suggestion box ──

function CorrectionGuidance({ lines }: { lines: string[] }) {
  return (
    <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mt-3 space-y-1">
      <div className="flex items-center gap-2 mb-1">
        <Lightbulb className="h-4 w-4 text-primary shrink-0" />
        <p className="text-xs font-semibold text-primary uppercase tracking-wide">How to Get Back on Track</p>
      </div>
      {lines.map((line, i) => (
        <p key={i} className="text-xs text-foreground/80 leading-relaxed">• {line}</p>
      ))}
    </div>
  );
}

// ── Helpers ──

function getNextAnniversaryDate(
  fromDate: string,
  anniversaryMonth: number,
  anniversaryDay: number
): string {
  const from = new Date(fromDate + "T00:00:00");
  const year = from.getFullYear();
  let anniv = new Date(year, anniversaryMonth - 1, anniversaryDay);
  if (anniv <= from) {
    anniv = new Date(year + 1, anniversaryMonth - 1, anniversaryDay);
  }
  return toISODate(anniv);
}

// ── Main component ──

export function StressTestAnalysis({ scenarioInput, combined, jurisdiction }: StressTestAnalysisProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { fmt } = createFormatter(jurisdiction);

  const baseRate = scenarioInput.policyLoanInput.policyLoanAnnualRate;
  const anniversaryMonth = scenarioInput.policyLoanInput.policyAnniversaryMonth;
  const anniversaryDay = scenarioInput.policyLoanInput.policyAnniversaryDay;

  const defaultNextAnniversary = getNextAnniversaryDate(
    scenarioInput.policyLoanInput.policyLoanEffectiveDate,
    anniversaryMonth,
    anniversaryDay
  );
  const defaultHigherRate = Math.round((baseRate * 100 + 1) * 10) / 10;

  // Scenario 1: Missed Payments
  const [missedCount, setMissedCount] = useState(3);
  const [missedStart, setMissedStart] = useState(6);
  const [showMissed, setShowMissed] = useState(false);

  // Scenario 2: Higher Rate
  const [newRate, setNewRate] = useState(defaultHigherRate);
  const [rateChangeDate, setRateChangeDate] = useState(defaultNextAnniversary);
  const [showRate, setShowRate] = useState(false);

  // Scenario 3: Delayed Start
  const [delayPeriods, setDelayPeriods] = useState(6);
  const [showDelay, setShowDelay] = useState(false);

  // Scenario 4: Variable Rate
  type VariableRatePreset = 'moderate' | 'covid' | 'shock1980s';
  const [variablePreset, setVariablePreset] = useState<VariableRatePreset>('moderate');
  const [showVariable, setShowVariable] = useState(false);

  const baseRatePct = baseRate * 100; // e.g. 6.5

  const variablePresets: Record<VariableRatePreset, { label: string; description: string; steps: VariableRateStep[] }> = {
    moderate: {
      label: "Moderate Adjustment",
      description: "A modest +0.50% rate increase at year 1, reflecting a routine carrier adjustment. Rate stays elevated.",
      steps: [
        { monthsFromStart: 12, annualRate: (baseRatePct + 0.50) / 100 },
      ],
    },
    covid: {
      label: "COVID-Era Variable Spike",
      description: "Models a scenario similar to 2020–2022 where variable-rate carriers (e.g., Manulife Financial) adjusted rates tied to prime. Rate spikes to ~9.25% for 2 years, then settles back to base + 0.25%.",
      steps: [
        { monthsFromStart: 6, annualRate: 0.08 },
        { monthsFromStart: 12, annualRate: 0.0925 },
        { monthsFromStart: 24, annualRate: 0.085 },
        { monthsFromStart: 36, annualRate: (baseRatePct + 0.25) / 100 },
      ],
    },
    shock1980s: {
      label: "1980s Rate Shock",
      description: "Models a prolonged high-rate environment. In Canada during the 1980s, policy loan rates reached approximately 10%. Rate climbs over 18 months, holds for 2 years, then gradually returns.",
      steps: [
        { monthsFromStart: 6, annualRate: 0.08 },
        { monthsFromStart: 12, annualRate: 0.09 },
        { monthsFromStart: 18, annualRate: 0.10 },
        { monthsFromStart: 42, annualRate: 0.09 },
        { monthsFromStart: 54, annualRate: (baseRatePct + 0.50) / 100 },
      ],
    },
  };

  const maxPayments = combined.plannedPayments.length;

  // Pick the correct stress test runner based on jurisdiction
  const stressRunner = jurisdiction === 'usa' ? runUSAStressTest : runStressTestCA;

  // Run stress tests
  const missedResult = useMemo(() => {
    if (!showMissed) return null;
    const scenario: StressScenarioInput = {
      type: "missed_payments",
      missedPayments: { numberOfMissedPayments: missedCount, startingPaymentNumber: missedStart },
    };
    return stressRunner(scenarioInput, combined.plannedPayments, combined.actualPolicyLoan, scenario);
  }, [showMissed, missedCount, missedStart, scenarioInput, combined, stressRunner]);

  const rateResult = useMemo(() => {
    if (!showRate) return null;
    const scenario: StressScenarioInput = {
      type: "higher_rate",
      higherRate: { newAnnualRate: newRate / 100, effectiveDate: rateChangeDate },
    };
    return stressRunner(scenarioInput, combined.plannedPayments, combined.actualPolicyLoan, scenario);
  }, [showRate, newRate, rateChangeDate, scenarioInput, combined, stressRunner]);

  const delayResult = useMemo(() => {
    if (!showDelay) return null;
    const scenario: StressScenarioInput = {
      type: "delayed_start",
      delayedStart: { delayPeriods },
    };
    return stressRunner(scenarioInput, combined.plannedPayments, combined.actualPolicyLoan, scenario);
  }, [showDelay, delayPeriods, scenarioInput, combined, stressRunner]);

  const selectedPreset = variablePresets[variablePreset];
  const variableResult = useMemo(() => {
    if (!showVariable) return null;
    const scenario: StressScenarioInput = {
      type: "variable_rate",
      variableRate: { label: selectedPreset.label, steps: selectedPreset.steps },
    };
    return stressRunner(scenarioInput, combined.plannedPayments, combined.actualPolicyLoan, scenario);
  }, [showVariable, variablePreset, selectedPreset, scenarioInput, combined, stressRunner]);

  const originalLedger = combined.actualPolicyLoan.ledger;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <Card className="border-dashed">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer select-none hover:bg-muted/30 transition-colors pb-2">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              <FlaskConical className="h-4 w-4" />
              Advanced Stress Test Analysis
            </CardTitle>
          </CardHeader>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <CardContent className="space-y-6 pt-0">
            <p className="text-sm text-muted-foreground">
              Use this section to model how missed payments, interest rate changes, or delayed repayment could affect loan repayment outcomes. This analysis is for planning and comparison purposes and does not replace the original repayment plan unless you choose to apply it.
            </p>

            {/* ── Scenario 1: Missed Payments ── */}
            <Collapsible>
              <div className="border border-border rounded-lg overflow-hidden">
                <CollapsibleTrigger asChild>
                  <button type="button" className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-destructive" />
                      1. Missed Payments Scenario
                    </h3>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4">
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>What this models:</strong> Life happens — a planned family vacation, a sabbatical, parental leave, a temporary job transition, or an unexpected expense may cause you to skip payments for a period.
                      </p>
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>Why it matters:</strong> Even a few missed payments can cause interest to compound significantly, pushing your payoff date further out and increasing total cost.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">Payments to skip: {missedCount}</Label>
                        <Slider value={[missedCount]} onValueChange={([v]) => setMissedCount(v)} min={1} max={Math.min(24, maxPayments)} step={1} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Starting at payment #: {missedStart}</Label>
                        <Slider value={[missedStart]} onValueChange={([v]) => setMissedStart(v)} min={1} max={maxPayments} step={1} />
                      </div>
                    </div>
                    <Button size="sm" variant={showMissed ? "default" : "outline"} onClick={() => setShowMissed(!showMissed)} className="w-full">
                      {showMissed ? "Hide Results" : "Run Scenario"}
                    </Button>
                    {showMissed && missedResult && (
                      <>
                        <ScenarioResultCard result={missedResult} jurisdiction={jurisdiction} />
                        <CorrectionGuidance lines={[
                          "Resume payments immediately after the missed period.",
                          `Consider making ${missedCount} additional catch-up payments to return to the original timeline.`,
                          "If a lump sum becomes available, apply it directly to reduce the compounded interest gap.",
                          "Even a partial payment during the missed period reduces interest capitalization.",
                          "Pre-plan for known disruptions: increase payments beforehand to build a buffer.",
                        ]} />
                        <ScenarioChart result={missedResult} label="Missed Payments" originalLedger={originalLedger} />
                      </>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* ── Scenario 2: Higher Rate ── */}
            <Collapsible>
              <div className="border border-border rounded-lg overflow-hidden">
                <CollapsibleTrigger asChild>
                  <button type="button" className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-destructive" />
                      2. Higher Interest Rate Scenario
                    </h3>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4">
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>What this models:</strong> Life insurance carriers may adjust their policy loan interest rate over time.
                      </p>
                      <div className="flex items-start gap-2 mt-1 p-2 bg-background rounded border border-border">
                        <Info className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                        <p className="text-xs text-foreground/70 leading-relaxed">
                          <strong>Note:</strong> Some carriers update their policy loan rate once per year on the policy anniversary date. Others may adjust more frequently as prime rates change.
                        </p>
                      </div>
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>Why it matters:</strong> A higher rate means each dollar of outstanding balance generates more daily interest.
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs">New rate: {newRate.toFixed(1)}%</Label>
                        <Slider value={[newRate * 10]} onValueChange={([v]) => setNewRate(v / 10)} min={Math.round(baseRate * 1000)} max={200} step={5} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Effective date (next anniversary: {defaultNextAnniversary})</Label>
                        <Input type="date" value={rateChangeDate} onChange={(e) => setRateChangeDate(e.target.value)} />
                      </div>
                    </div>
                    <Button size="sm" variant={showRate ? "default" : "outline"} onClick={() => setShowRate(!showRate)} className="w-full">
                      {showRate ? "Hide Results" : "Run Scenario"}
                    </Button>
                    {showRate && rateResult && (
                      <>
                        <ScenarioResultCard result={rateResult} jurisdiction={jurisdiction} />
                        <CorrectionGuidance lines={[
                          "Increase your periodic payment to offset the higher interest.",
                          "Consider shortening the term and increasing payments to limit exposure.",
                          "If you have available cash, a lump sum reduces the principal the higher rate applies to.",
                          "Review your policy annually and request an updated loan rate from your carrier.",
                        ]} />
                        <ScenarioChart result={rateResult} label="Higher Rate" originalLedger={originalLedger} />
                      </>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* ── Scenario 3: Delayed Start ── */}
            <Collapsible>
              <div className="border border-border rounded-lg overflow-hidden">
                <CollapsibleTrigger asChild>
                  <button type="button" className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Clock className="h-4 w-4 text-destructive" />
                      3. Delayed Start Scenario
                    </h3>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4">
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>What this models:</strong> Sometimes borrowers delay starting their repayment schedule.
                      </p>
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>Why it matters:</strong> Interest accrues from day one of the loan, whether or not payments have started.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Delay by: {delayPeriods} period(s)</Label>
                      <Slider value={[delayPeriods]} onValueChange={([v]) => setDelayPeriods(v)} min={1} max={24} step={1} />
                    </div>
                    <Button size="sm" variant={showDelay ? "default" : "outline"} onClick={() => setShowDelay(!showDelay)} className="w-full">
                      {showDelay ? "Hide Results" : "Run Scenario"}
                    </Button>
                    {showDelay && delayResult && (
                      <>
                        <ScenarioResultCard result={delayResult} jurisdiction={jurisdiction} />
                        <CorrectionGuidance lines={[
                          "Start repayment as soon as possible — every day of delay adds interest.",
                          "If you must delay, consider paying at least the accruing interest.",
                          "Once you begin payments, consider increasing the amount to make up for the delay.",
                        ]} />
                        <ScenarioChart result={delayResult} label="Delayed Start" originalLedger={originalLedger} />
                      </>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>

            {/* ── Scenario 4: Variable Rate ── */}
            <Collapsible>
              <div className="border border-border rounded-lg overflow-hidden">
                <CollapsibleTrigger asChild>
                  <button type="button" className="w-full flex items-center justify-between p-4 hover:bg-muted/30 transition-colors">
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                      <Activity className="h-4 w-4 text-destructive" />
                      4. Variable Rate Scenario
                    </h3>
                    <ChevronRight className="h-4 w-4 text-muted-foreground" />
                  </button>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="p-4 pt-0 space-y-4">
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>What this models:</strong> Some carriers use variable policy loan rates tied to prime or adjusted periodically.
                      </p>
                      <div className="flex items-start gap-2 mt-1 p-2 bg-background rounded border border-border">
                        <Info className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                        <p className="text-xs text-foreground/70 leading-relaxed">
                          <strong>Historical context:</strong> In Canada during the 1980s, policy loan rates reached ~10%. During COVID-era (2020–2022), some variable-rate carriers adjusted rates up to ~9.25%.
                        </p>
                      </div>
                      <p className="text-xs text-foreground/80 leading-relaxed">
                        <strong>Why it matters:</strong> If your carrier uses a variable rate, your repayment plan may be affected by rate changes you can't control.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Select a rate scenario:</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {(Object.keys(variablePresets) as VariableRatePreset[]).map((key) => {
                          const preset = variablePresets[key];
                          const isActive = variablePreset === key;
                          return (
                            <button
                              key={key}
                              onClick={() => setVariablePreset(key)}
                              className={`text-left rounded-lg border p-3 transition-colors ${
                                isActive ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/50'
                              }`}
                            >
                              <p className={`text-xs font-semibold ${isActive ? 'text-primary' : 'text-foreground'}`}>{preset.label}</p>
                              <p className="text-[10px] text-muted-foreground mt-1 leading-relaxed">{preset.description}</p>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                    <div className="bg-muted/30 rounded-lg p-3 space-y-2">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Rate Trajectory (Applied at Policy Anniversary)</p>
                      <p className="text-[10px] text-muted-foreground leading-relaxed">
                        Variable rates only take effect on your policy anniversary date.
                      </p>
                      <div className="flex flex-wrap gap-1 items-center">
                        <span className="text-xs bg-background border border-border rounded px-2 py-0.5">
                          Start: {(baseRate * 100).toFixed(2)}%
                        </span>
                        {selectedPreset.steps.map((step, i) => {
                          const rawDate = new Date(new Date(scenarioInput.policyLoanInput.policyLoanEffectiveDate + "T00:00:00"));
                          rawDate.setMonth(rawDate.getMonth() + step.monthsFromStart);
                          const year = rawDate.getFullYear();
                          let anniv = new Date(year, anniversaryMonth - 1, anniversaryDay);
                          if (anniv <= rawDate) anniv = new Date(year + 1, anniversaryMonth - 1, anniversaryDay);
                          const annivStr = anniv.toISOString().slice(0, 10);
                          return (
                            <span key={i} className="text-xs flex items-center gap-1">
                              <span className="text-muted-foreground">→</span>
                              <span className={`rounded px-2 py-0.5 border ${
                                step.annualRate > baseRate + 0.02 ? 'bg-destructive/10 border-destructive/30 text-destructive' : 'bg-background border-border'
                              }`}>
                                {annivStr}: {(step.annualRate * 100).toFixed(2)}%
                              </span>
                            </span>
                          );
                        })}
                      </div>
                    </div>
                    <Button size="sm" variant={showVariable ? "default" : "outline"} onClick={() => setShowVariable(!showVariable)} className="w-full">
                      {showVariable ? "Hide Results" : "Run Scenario"}
                    </Button>
                    {showVariable && variableResult && (
                      <>
                        <ScenarioResultCard result={variableResult} jurisdiction={jurisdiction} />
                        <CorrectionGuidance lines={[
                          "Build a buffer into your payment — commit to a slightly higher payment than the minimum.",
                          "Monitor your carrier's rate announcements annually and adjust your commitment if rates move significantly.",
                          "Consider periodic lump-sum payments to reduce the principal exposed to rate fluctuations.",
                          "A fixed-rate carrier eliminates this risk entirely — ask your advisor about your options.",
                          "Even during high-rate periods, consistent repayment discipline prevents the balance from growing out of control.",
                        ]} />
                        <ScenarioChart result={variableResult} label={selectedPreset.label} originalLedger={originalLedger} />
                      </>
                    )}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
